import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { PropTypes } from 'prop-types';
import { IconButton, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';

import CloseIcon from '@material-ui/icons/CloseSharp';
import { useMeContext } from '~/modules/me';
import { DelaStarIcon } from '~/modules/common/components/Icons';
import StickyHeader from '~/modules/common/components/StickyHeader';
import { useSessionStorage } from '~/modules/common/hooks/useSessionStorage';
import { useGetResume } from '~/modules/resource-management/resourceUserDetails/ResumeCard/useResumeHandlers';
import useStyles, { useStickyHeaderStyles } from './useStyles';

const UserProfileAttachmentsBanner = ({
  classes: classesOverrides,
  userUri,
  showProfileButton = true
}) => {
  const classes = useStyles({ classes: classesOverrides });
  const stickyHeaderClasses = useStickyHeaderStyles();
  const intl = useIntl();

  const {
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();

  const { storedValue: isDisplayEnabled, setValue } = useSessionStorage(
    `${userUri}resume-banner-display`,
    false
  );

  const onClose = useCallback(() => {
    setValue(true);
  }, [setValue]);

  const { data, isLoading } = useGetResume({
    userUri
  });

  const hasResume = data?.user?.resume;

  const profileLinkTo = useMemo(
    () => ({
      pathname: '/profile',
      state: { showBackButton: true }
    }),
    []
  );

  const title = useMemo(
    () => (
      <div className={classes.container}>
        <DelaStarIcon />
        <Typography className={classes.bannerText} component="h2">
          <FormattedMessage id="userProfile.banner" />
        </Typography>
        {showProfileButton && (
          <Link className={classes.link} to={profileLinkTo}>
            <Typography
              data-qe-id="ViewProfileText"
              variant="button"
              color="primary"
            >
              <FormattedMessage id="userProfile.tryItNow" />
            </Typography>
          </Link>
        )}
      </div>
    ),
    [
      classes.bannerText,
      classes.container,
      classes.link,
      showProfileButton,
      profileLinkTo
    ]
  );

  return isPsaPraaResourceMatchingEnabled &&
    !isLoading &&
    !hasResume &&
    !isDisplayEnabled ? (
    <StickyHeader
      bottomBorder
      title={title}
      level={1}
      classes={stickyHeaderClasses}
    >
      <IconButton
        color="inherit"
        onClick={onClose}
        aria-label={intl.formatMessage({
          id: 'userProfile.closeIcon'
        })}
      >
        <span
          aria-label={intl.formatMessage({
            id: 'userProfile.closeButton'
          })}
          role="img"
        >
          <CloseIcon />
        </span>
      </IconButton>
    </StickyHeader>
  ) : null;
};

UserProfileAttachmentsBanner.propTypes = {
  userUri: PropTypes.string.isRequired,
  showProfileButton: PropTypes.bool,
  classes: PropTypes.object
};

export default UserProfileAttachmentsBanner;
